import { ArrowUpRight, ArrowDownRight, Download, Repeat } from "lucide-react";
import Sparkline from "./Sparkline";

const fmtPct = (n) => {
  if (n == null || isNaN(n)) return "—";
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

const fmtMoney = (n, currency = "USD") =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(n);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
};

const INTERVAL_BADGE = {
  ipo: "01",
  since_2020: "02",
  fy2025: "03",
  ytd: "04",
};

function ResultCard({ r, currency, mode }) {
  const reinvest = mode === "reinvest";
  const totalReturn = reinvest ? r.total_return_reinvest_pct : r.total_return_pct;
  const cagr = reinvest ? r.cagr_reinvest_pct : r.cagr_simple_pct;
  const up = (totalReturn ?? 0) >= 0;
  const ChangeIcon = up ? ArrowUpRight : ArrowDownRight;
  const priceUp = (r.price_return_pct ?? 0) >= 0;

  return (
    <div
      data-testid={`result-card-${r.interval_id}`}
      className="border border-zinc-800 bg-[#121214] flex flex-col"
    >
      {/* HEADER — bigger, more obvious */}
      <div className="border-b border-zinc-900 px-5 py-5 bg-[#0e0e10]">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {INTERVAL_BADGE[r.interval_id] || "•"} · INTERVAL
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {r.years}y · {r.dividend_count} div
          </span>
        </div>
        <div
          className="text-xl sm:text-2xl font-bold tracking-tight text-white"
          data-testid={`result-card-title-${r.interval_id}`}
        >
          {r.label}
        </div>
        <div className="font-mono text-xs text-zinc-400 mt-1">
          {fmtDate(r.start_date)} → {fmtDate(r.end_date)}
        </div>
      </div>

      {/* Big total return */}
      <div className="px-5 py-5">
        <div className="text-label mb-1">
          {reinvest ? "Total Return (Reinvested)" : "Total Return (Simple)"}
        </div>
        <div
          className={`font-mono text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2 ${
            up ? "text-[#00E676]" : "text-[#FF3B30]"
          }`}
          data-testid={`total-return-${r.interval_id}`}
        >
          <ChangeIcon size={22} />
          {fmtPct(totalReturn)}
        </div>
        <div className="text-[11px] font-mono text-zinc-500 mt-1">
          CAGR {fmtPct(cagr)}
        </div>
      </div>

      {/* Sparkline */}
      <div className="px-5 pb-2">
        <Sparkline data={r.sparkline} height={56} />
      </div>

      {/* Breakdown */}
      <div className="border-t border-zinc-900 divide-y divide-zinc-900 mt-auto">
        <Row label="Start Price" value={fmtMoney(r.start_price, currency)} mono />
        <Row label="End Price" value={fmtMoney(r.end_price, currency)} mono />
        <Row
          label="Price Return"
          value={fmtPct(r.price_return_pct)}
          mono
          color={priceUp ? "text-[#00E676]" : "text-[#FF3B30]"}
        />
        <Row
          label="Dividends Received"
          value={fmtMoney(r.dividends_received, currency)}
          mono
          color="text-[#FFD60A]"
        />
        <Row
          label="Dividend Return"
          value={fmtPct(r.dividend_return_pct)}
          mono
          color="text-[#FFD60A]"
        />
      </div>
    </div>
  );
}

function Row({ label, value, color = "", mono = false }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <span className="text-label">{label}</span>
      <span className={`${mono ? "font-mono" : ""} text-sm ${color}`}>{value}</span>
    </div>
  );
}

function downloadCSV(data, mode) {
  if (!data || !data.results) return;
  const hdr = [
    "Symbol",
    "Interval",
    "Start Date",
    "End Date",
    "Years",
    "Start Price",
    "End Price",
    "Price Return %",
    "Dividends Received",
    "Dividend Count",
    "Dividend Return %",
    "Total Return (Simple) %",
    "Total Return (Reinvested) %",
    "CAGR Simple %",
    "CAGR Reinvested %",
  ];
  const rows = data.results.map((r) => [
    data.symbol,
    r.label,
    r.start_date,
    r.end_date,
    r.years,
    r.start_price,
    r.end_price,
    r.price_return_pct,
    r.dividends_received,
    r.dividend_count,
    r.dividend_return_pct,
    r.total_return_pct,
    r.total_return_reinvest_pct,
    r.cagr_simple_pct ?? "",
    r.cagr_reinvest_pct ?? "",
  ]);
  const csv = [hdr, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${data.symbol}_total_return_${mode}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ResultsPanel({ data, loading, currency, mode, onChangeMode }) {
  if (loading) {
    return (
      <div data-testid="results-panel" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-96 border border-zinc-800 bg-[#121214] p-6 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!data || !data.results || data.results.length === 0) {
    return (
      <div className="border border-zinc-800 bg-[#121214] p-8 text-center text-sm text-zinc-500">
        No results computed.
      </div>
    );
  }

  const cols = data.results.length;
  let gridCols = "grid-cols-1";
  if (cols === 2) gridCols = "grid-cols-1 md:grid-cols-2";
  else if (cols === 3) gridCols = "grid-cols-1 md:grid-cols-3";
  else if (cols >= 4) gridCols = "grid-cols-1 md:grid-cols-2 xl:grid-cols-4";

  return (
    <div>
      {/* Mode toggle + CSV export */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div
          className="inline-flex border border-zinc-800 bg-[#0d0d0f]"
          data-testid="mode-toggle"
        >
          <button
            data-testid="mode-simple-btn"
            onClick={() => onChangeMode("simple")}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
              mode === "simple"
                ? "bg-white text-black"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            Simple TR
          </button>
          <button
            data-testid="mode-reinvest-btn"
            onClick={() => onChangeMode("reinvest")}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors inline-flex items-center gap-1.5 ${
              mode === "reinvest"
                ? "bg-white text-black"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <Repeat size={12} /> Reinvested (DRIP)
          </button>
        </div>
        <button
          data-testid="csv-export-btn"
          onClick={() => downloadCSV(data, mode)}
          className="inline-flex items-center gap-2 border border-zinc-800 bg-transparent hover:bg-zinc-900 text-zinc-300 hover:text-white px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      <div data-testid="results-panel" className={`grid ${gridCols} gap-4`}>
        {data.results.map((r) => (
          <ResultCard
            key={r.interval_id}
            r={r}
            currency={currency || data.currency}
            mode={mode}
          />
        ))}
      </div>
    </div>
  );
}
